import { CSSProperties, ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

/**
 * Props for the drag grip. Derived from useSortable's own return type rather than spelled out —
 * dnd-kit exposes the listener map only through an internal path, and a hand-written shape would
 * drift on upgrade.
 */
type SortableResult = ReturnType<typeof useSortable>

export interface HandleProps {
  attributes: SortableResult['attributes']
  listeners: SortableResult['listeners']
}

interface Props {
  id: number
  disabled?: boolean
  children: (handle: HandleProps) => ReactNode
}

/**
 * A <tr> that can be dragged. The grip is not rendered here: the caller receives the handle props
 * and decides which cell carries them, so a screen can put the grip wherever its columns need it.
 */
export const SortableTableRow = ({ id, disabled = false, children }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    // dnd-kit 5 accepts string ids only; the domain keeps numbers, so convert at the boundary.
    id: String(id),
    disabled,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // The dragged row rides above its neighbours; without this the striped rows below paint over it.
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 1 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  }

  return (
    <tr ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </tr>
  )
}
